import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  useCreateWhiteLabelThemeMutation,
  useGetWhiteLabelThemeByCompanyQuery,
  useUpdateWhiteLabelThemeMutation,
} from "../../../redux/admin/whiteLabelApi";
import WhiteLabelWorkspaceShell from "../Components/WhiteLabelWorkspaceShell";
import {
  getApiErrorMessage,
  HEX_COLOR_REGEX,
  mapValidationErrors,
} from "../Utils/whiteLabelFormUtils";
import { toast } from "react-hot-toast";

const SIDEBAR_STYLES = ["default", "compact", "floating", "glass"];
const HEADER_STYLES = ["default", "elevated", "minimal", "glass"];
const SHADOW_STYLES = ["none", "soft", "medium", "strong"];

const EMPTY_FORM = Object.freeze({
  themeName: "",
  darkModeEnabled: true,
  compactLayout: false,
  colors: {
    primary: "#2563EB",
    secondary: "#0F172A",
    accent: "#14B8A6",
    sidebar: "#0F172A",
    header: "#FFFFFF",
    background: "#F8FAFC",
    cardBackground: "#FFFFFF",
    border: "#E2E8F0",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    success: "#16A34A",
    warning: "#D97706",
    error: "#DC2626",
  },
  typography: {
    fontFamily: "Roboto",
    fontSizeScale: 1,
  },
  layout: {
    sidebarStyle: "default",
    headerStyle: "default",
    borderRadius: 12,
    shadowStyle: "soft",
  },
});

function normalizeThemeRecord(themeRecord) {
  if (!themeRecord) return EMPTY_FORM;
  return {
    themeName: themeRecord.themeName || "",
    darkModeEnabled: Boolean(themeRecord.darkModeEnabled),
    compactLayout: Boolean(themeRecord.compactLayout),
    colors: {
      ...EMPTY_FORM.colors,
      ...(themeRecord.colors || {}),
    },
    typography: {
      ...EMPTY_FORM.typography,
      ...(themeRecord.typography || {}),
    },
    layout: {
      ...EMPTY_FORM.layout,
      ...(themeRecord.layout || {}),
    },
  };
}

function validateForm(form) {
  const errors = {};

  Object.entries(form.colors || {}).forEach(([key, value]) => {
    if (value && !HEX_COLOR_REGEX.test(value)) {
      errors[`colors.${key}`] = "Enter a valid hex color.";
    }
  });

  const fontScale = Number(form.typography?.fontSizeScale);
  if (!Number.isFinite(fontScale) || fontScale < 0.75 || fontScale > 1.5) {
    errors["typography.fontSizeScale"] =
      "Font size scale must be between 0.75 and 1.5.";
  }

  const borderRadius = Number(form.layout?.borderRadius);
  if (!Number.isFinite(borderRadius) || borderRadius < 0 || borderRadius > 40) {
    errors["layout.borderRadius"] =
      "Border radius must be between 0 and 40.";
  }

  return errors;
}

function buildPayload(companyId, form) {
  return {
    companyId,
    themeName: String(form.themeName || "").trim() || undefined,
    darkModeEnabled: Boolean(form.darkModeEnabled),
    compactLayout: Boolean(form.compactLayout),
    colors: form.colors,
    typography: {
      fontFamily: String(form.typography?.fontFamily || "").trim() || undefined,
      fontSizeScale: Number(form.typography?.fontSizeScale),
    },
    layout: {
      sidebarStyle: form.layout?.sidebarStyle,
      headerStyle: form.layout?.headerStyle,
      borderRadius: Number(form.layout?.borderRadius),
      shadowStyle: form.layout?.shadowStyle,
    },
  };
}

export default function WhiteLabelThemePage() {
  const muiTheme = useTheme();
  const [companyId, setCompanyId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    data: themeRecord,
    isFetching,
    error,
    refetch,
  } = useGetWhiteLabelThemeByCompanyQuery(companyId, {
    skip: !companyId,
  });

  const [createTheme, { isLoading: isCreating }] =
    useCreateWhiteLabelThemeMutation();
  const [updateTheme, { isLoading: isUpdating }] =
    useUpdateWhiteLabelThemeMutation();

  useEffect(() => {
    if (themeRecord) {
      setForm(normalizeThemeRecord(themeRecord));
      setFieldErrors({});
      setSubmitError("");
    }
  }, [themeRecord]);

  useEffect(() => {
    if (!companyId) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setSubmitError("");
    }
  }, [companyId]);

  const isMissingRecord = error?.status === 404;
  const errorMessage =
    error && !isMissingRecord
      ? getApiErrorMessage(error, "Failed to load theme.")
      : "";

  const previewStyles = useMemo(
    () => ({
      background: form.colors.background,
      color: form.colors.textPrimary,
      borderRadius: `${form.layout.borderRadius}px`,
      boxShadow:
        form.layout.shadowStyle === "none"
          ? "none"
          : form.layout.shadowStyle === "soft"
            ? "0 12px 24px rgba(15,23,42,0.08)"
            : form.layout.shadowStyle === "medium"
              ? "0 16px 32px rgba(15,23,42,0.12)"
              : "0 22px 40px rgba(15,23,42,0.18)",
    }),
    [form]
  );

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      const payload = buildPayload(companyId, form);
      if (isMissingRecord) {
        await createTheme(payload).unwrap();
        toast.success("Theme created successfully.");
      } else {
        await updateTheme(payload).unwrap();
        toast.success("Theme updated successfully.");
      }
      setSubmitError("");
      refetch();
    } catch (submitErrorValue) {
      const validationErrors = mapValidationErrors(submitErrorValue);
      setFieldErrors((prev) => ({ ...prev, ...validationErrors }));
      const message = getApiErrorMessage(
        submitErrorValue,
        "Unable to save theme."
      );
      setSubmitError(message);
      toast.error(message);
    }
  };

  const setColorField = (field, value) =>
    setForm((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [field]: value,
      },
    }));

  return (
    <WhiteLabelWorkspaceShell
      title="Theme Manager"
      description="Manage company-specific theme configuration using the existing Theme backend foundation."
      companyId={companyId}
      onCompanyChange={setCompanyId}
      error={errorMessage}
      actions={
        <Chip
          label={isMissingRecord ? "Theme Not Created" : "Live Preview Ready"}
          color={isMissingRecord ? "warning" : "success"}
          variant={isMissingRecord ? "outlined" : "filled"}
          sx={{ fontWeight: 800 }}
        />
      }
    >
      {!companyId ? (
        <Alert severity="info">Select a company to manage theme settings.</Alert>
      ) : isFetching ? (
        <Alert severity="info">Loading theme configuration...</Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, xl: 8 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: { xs: 2, md: 2.5 },
                border: `1px solid ${alpha(muiTheme.palette.divider, 0.88)}`,
              }}
            >
              <Stack spacing={2}>
                {isMissingRecord ? (
                  <Alert severity="info">
                    This company does not have a theme record yet. Saving will create it.
                  </Alert>
                ) : null}
                {submitError ? <Alert severity="error">{submitError}</Alert> : null}

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Theme Name"
                      value={form.themeName}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, themeName: event.target.value }))
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.darkModeEnabled}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              darkModeEnabled: event.target.checked,
                            }))
                          }
                        />
                      }
                      label="Dark Mode"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.compactLayout}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              compactLayout: event.target.checked,
                            }))
                          }
                        />
                      }
                      label="Compact Layout"
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Color System
                </Typography>
                <Grid container spacing={2}>
                  {Object.keys(form.colors).map((field) => (
                    <Grid key={field} size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label={field}
                        value={form.colors[field]}
                        onChange={(event) => setColorField(field, event.target.value)}
                        error={Boolean(fieldErrors[`colors.${field}`])}
                        helperText={fieldErrors[`colors.${field}`] || " "}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Typography
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Font Family"
                      value={form.typography.fontFamily}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          typography: {
                            ...prev.typography,
                            fontFamily: event.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ min: 0.75, max: 1.5, step: 0.05 }}
                      label="Font Size Scale"
                      value={form.typography.fontSizeScale}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          typography: {
                            ...prev.typography,
                            fontSizeScale: event.target.value,
                          },
                        }))
                      }
                      error={Boolean(fieldErrors["typography.fontSizeScale"])}
                      helperText={fieldErrors["typography.fontSizeScale"] || " "}
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Layout
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      select
                      fullWidth
                      label="Sidebar Style"
                      value={form.layout.sidebarStyle}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          layout: {
                            ...prev.layout,
                            sidebarStyle: event.target.value,
                          },
                        }))
                      }
                    >
                      {SIDEBAR_STYLES.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      select
                      fullWidth
                      label="Header Style"
                      value={form.layout.headerStyle}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          layout: {
                            ...prev.layout,
                            headerStyle: event.target.value,
                          },
                        }))
                      }
                    >
                      {HEADER_STYLES.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Border Radius"
                      value={form.layout.borderRadius}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          layout: {
                            ...prev.layout,
                            borderRadius: event.target.value,
                          },
                        }))
                      }
                      error={Boolean(fieldErrors["layout.borderRadius"])}
                      helperText={fieldErrors["layout.borderRadius"] || " "}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      select
                      fullWidth
                      label="Shadow Style"
                      value={form.layout.shadowStyle}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          layout: {
                            ...prev.layout,
                            shadowStyle: event.target.value,
                          },
                        }))
                      }
                    >
                      {SHADOW_STYLES.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isCreating || isUpdating}
                  >
                    {isCreating || isUpdating
                      ? "Saving..."
                      : isMissingRecord
                        ? "Create Theme"
                        : "Save Theme"}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, xl: 4 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2.5,
                border: `1px solid ${alpha(muiTheme.palette.divider, 0.88)}`,
                ...previewStyles,
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: `${form.layout.borderRadius}px`,
                    bgcolor: form.colors.header,
                    color: form.colors.textPrimary,
                    border: `1px solid ${alpha(form.colors.border, 0.85)}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 900,
                      fontFamily: form.typography.fontFamily,
                      fontSize: `${form.typography.fontSizeScale}rem`,
                    }}
                  >
                    {form.themeName || "Theme Preview"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: form.colors.textSecondary }}
                  >
                    Live preview based on the current form state.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: `${form.layout.borderRadius}px`,
                    bgcolor: form.colors.cardBackground,
                    border: `1px solid ${alpha(form.colors.border, 0.9)}`,
                  }}
                >
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {["primary", "secondary", "accent", "success", "warning", "error"].map((field) => (
                      <Chip
                        key={field}
                        label={field}
                        sx={{
                          bgcolor: form.colors[field],
                          color: "#fff",
                          fontWeight: 700,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </WhiteLabelWorkspaceShell>
  );
}
